/**
 * Workplan Reports - Keystone RFP Agent
 * Enhanced wrapper for workplan management with search, builder, and entity type subtabs
 * Similar structure to ComplianceReports.tsx but for workplans
 */

import React, { useState, useMemo, useEffect } from 'react';
import { Card, Button, Input, Select, Badge, Modal, Tabs } from '@tracekit/core';
import { 
  Filter, X, Copy, Archive, Download, Share2, Trash2, MoreVertical, 
  Search, FileText, Lightbulb, Building2, Database, Plus, ListChecks
} from 'lucide-react';
import { listReports, downloadReportPDF, downloadReportV2, getReport, submitReportJob, updateReportStatus } from '@tracekit/core';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useWorkspace, useToastStore } from '@tracekit/core';
import { Report } from '@tracekit/core';
import { WorkplanSearchPage } from '../pages/WorkplanSearchPage';
import { TechnicalApproachesPage } from '../pages/TechnicalApproachesPage';
import { ProjectPortfolioPage } from '../pages/ProjectPortfolioPage';
import { CanonicalDataPage } from '../pages/CanonicalDataPage';
import { WorkplanBuilder } from './WorkplanBuilder';
import { WorkplanGenerationProvider } from '../contexts/WorkplanGenerationContext';
import { WorkplanTemplateSelector, WorkplanTemplate } from './WorkplanTemplateSelector';
import { queryEntities, getProject, getPolicyPack } from '@tracekit/core';
import { EmptyState, LoadingSpinner } from '@tracekit/core';
import { Calendar, User, Tag, Grid, List as ListIcon } from 'lucide-react';

interface FilterState {
  dateFrom: string;
  dateTo: string;
  client: string;
  projectType: string;
  status: string;
  serviceLine: string;
}

interface WorkplanReportsProps {
  initialReportId?: string; // Optional report ID to open in viewer on mount
}

export function WorkplanReports({ initialReportId }: WorkplanReportsProps = {}) {
  const { projectId } = useWorkspace();
  const queryClient = useQueryClient();
  const { addToast } = useToastStore();
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState<FilterState>({
    dateFrom: '',
    dateTo: '',
    client: '',
    projectType: '',
    status: 'all',
    serviceLine: 'all',
  });
  const [selectedReportId, setSelectedReportId] = useState<string | null>(null);
  const [actionMenuOpen, setActionMenuOpen] = useState<string | null>(null);
  const [activeMainTab, setActiveMainTab] = useState('search');
  const [activeSearchSubtab, setActiveSearchSubtab] = useState('workplans');
  const [selectedWorkplanReport, setSelectedWorkplanReport] = useState<Report | null>(null);
  const [selectedTemplate, setSelectedTemplate] = useState<{
    serviceLine: string;
    template: any;
  } | null>(null);
  const [creationMethod, setCreationMethod] = useState<'report' | 'template'>('template');
  const [builderViewMode, setBuilderViewMode] = useState<'grid' | 'list'>('grid');
  const [builderSortBy, setBuilderSortBy] = useState<'date' | 'name' | 'client' | 'serviceLine'>('date');
  const [builderSortDirection, setBuilderSortDirection] = useState<'asc' | 'desc'>('desc');
  const [builderSearchQuery, setBuilderSearchQuery] = useState('');
  const [previewTemplate, setPreviewTemplate] = useState<{ serviceLine: string; template: any } | null>(null);

  // Get tab from URL query params
  const urlParams = new URLSearchParams(window.location.search);
  const urlTab = urlParams.get('tab') || 'search';
  const urlSubtab = urlParams.get('subtab') || 'workplans';

  useEffect(() => {
    if (urlTab) setActiveMainTab(urlTab);
    if (urlSubtab) setActiveSearchSubtab(urlSubtab);
  }, [urlTab, urlSubtab]);

  // Fetch reports with workplan-specific filtering (only workplan_generation on server side)
  const { data: reports } = useQuery({
    queryKey: ['reports', projectId, 'workplans'],
    queryFn: () => listReports(projectId!, { report_type: 'workplan_generation' }),
    enabled: !!projectId,
  });

  // Fetch project to get policy pack ID
  const { data: project } = useQuery({
    queryKey: ['project', projectId],
    queryFn: () => getProject(projectId!),
    enabled: !!projectId && activeMainTab === 'builder',
  });

  // Fetch policy pack to get workplan templates
  const { data: policyPack } = useQuery({
    queryKey: ['policy-pack', project?.policy_pack_id],
    queryFn: () => getPolicyPack(project!.policy_pack_id!),
    enabled: !!project?.policy_pack_id && activeMainTab === 'builder' && creationMethod === 'template',
  });

  // Extract and audit workplan templates from policy pack
  const workplanTemplates = useMemo(() => {
    if (!policyPack) return [];
    const rules = policyPack.rules || policyPack.policy_data || {};
    const reportTypes = rules.report_types || [];
    const workplanReportType = reportTypes.find((rt: any) => rt.id === 'workplan_generation');
    if (!workplanReportType?.service_line_templates) return [];
    
    const rawTemplates: WorkplanTemplate[] = Object.entries(workplanReportType.service_line_templates).map(([key, template]: [string, any]) => ({
      key,
      serviceLine: template.service_line || key.replace(/_/g, ' ').replace(/\b\w/g, (l: string) => l.toUpperCase()),
      template,
    }));
    
    // Templates are audited and filtered in WorkplanTemplateSelector component
    return rawTemplates;
  }, [policyPack]);

  // Filter reports based on workplan-specific criteria
  const filteredReports = useMemo(() => {
    if (!reports) return [];
    
    return reports.filter((report: Report) => {
      // Filter by report type (workplan reports)
      const metadata = (report as any).metadata || {};
      const isWorkplan = 
        report.report_type === 'workplan_generation' ||
        (report.report_type === 'custom' && 
         metadata.report_type_override === 'workplan_generation');
      
      if (!isWorkplan) return false;

      // Apply filters
      const reportName = report.name || report.client_name;
      if (filters.client && !reportName?.toLowerCase().includes(filters.client.toLowerCase())) {
        return false;
      }
      if (filters.status !== 'all' && report.status !== filters.status) {
        return false;
      }
      if (filters.dateFrom && new Date(report.created_at) < new Date(filters.dateFrom)) {
        return false;
      }
      if (filters.dateTo && new Date(report.created_at) > new Date(filters.dateTo)) {
        return false;
      }
      if (filters.projectType && !metadata.project_type?.toLowerCase().includes(filters.projectType.toLowerCase())) {
        return false;
      }
      if (filters.serviceLine && !metadata.service_line?.toLowerCase().includes(filters.serviceLine.toLowerCase())) {
        return false;
      }
      
      return true;
    });
  }, [reports, filters]);

  const handleClearFilters = () => {
    setFilters({
      dateFrom: '',
      dateTo: '',
      client: '',
      projectType: '',
      status: 'all',
      serviceLine: 'all',
    });
  };

  // Quick actions handlers (matching ComplianceReports pattern)
  const updateStatusMutation = useMutation({
    mutationFn: async ({ reportId, status }: { reportId: string; status: string }) => {
      return updateReportStatus(reportId, status as any);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['reports', projectId] });
      setActionMenuOpen(null);
      addToast({
        type: 'success',
        title: 'Status updated',
        message: 'Workplan status has been updated successfully',
      });
    },
    onError: (error: any) => {
      addToast({
        type: 'error',
        title: 'Failed to update status',
        message: error.message || 'Could not update workplan status',
      });
    },
  });

  const handleClone = async (reportId: string) => {
    try {
      const report = reports?.find((r: Report) => r.id === reportId);
      if (!report) {
        addToast({
          type: 'error',
          title: 'Report not found',
          message: 'Cannot clone - report not found',
        });
        return;
      }

      const fullReport = await getReport(reportId);
      const questions = (fullReport.sections || []).map((s: any) => ({
        section: s.section_name,
        question: s.question,
      }));

      if (questions.length === 0) {
        addToast({
          type: 'error',
          title: 'Cannot clone',
          message: 'Report has no sections to clone',
        });
        return;
      }

      const metadata = (report as any).metadata || {};
      await submitReportJob({
        project_id: report.project_id,
        report_type: 'custom',
        client_name: report.client_name,
        name: `${report.name || report.client_name} (Copy)`,
        questions,
        metadata: {
          ...metadata,
          cloned_from: reportId,
          report_type_override: 'workplan_generation',
        },
        enable_cache: true,
      });

      addToast({
        type: 'success',
        title: 'Workplan cloned',
        message: 'A new workplan has been created with the same content',
      });
      setActionMenuOpen(null);
    } catch (error: any) {
      addToast({
        type: 'error',
        title: 'Clone failed',
        message: error.message || 'Failed to clone workplan',
      });
    }
  };

  const handleArchive = async (reportId: string) => {
    if (confirm('Archive this workplan?')) {
      await updateStatusMutation.mutateAsync({ reportId, status: 'archived' });
    }
  };

  const handleExport = async (reportId: string, format: 'pdf' | 'markdown') => {
    try {
      if (format === 'pdf') {
        const blob = await downloadReportPDF(reportId);
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `workplan-${reportId}.pdf`;
        a.click();
        window.URL.revokeObjectURL(url);
      } else {
        const content = await downloadReportV2(reportId);
        const blob = new Blob([content], { type: 'text/markdown' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `workplan-${reportId}.md`;
        a.click();
        window.URL.revokeObjectURL(url);
      }
      setActionMenuOpen(null);
    } catch (error) {
      console.error('Export failed:', error);
      addToast({
        type: 'error',
        title: 'Export failed',
        message: (error as Error).message || 'Failed to export workplan',
      });
    }
  };

  const handleDelete = async (reportId: string) => {
    if (confirm('Archive this workplan? It will be moved to archived status.')) {
      try {
        await updateStatusMutation.mutateAsync({ reportId, status: 'archived' });
        addToast({
          type: 'success',
          title: 'Workplan archived',
          message: 'The workplan has been moved to archived status',
        });
      } catch (error: any) {
        addToast({
          type: 'error',
          title: 'Archive failed',
          message: error.message || 'Failed to archive workplan',
        });
      }
    }
  };

  const activeFilterCount = Object.values(filters).filter(v => v && v !== 'all').length;

  // Check if we're viewing a specific workplan (hide filters)
  const [isViewingWorkplan, setIsViewingWorkplan] = useState(false);
  
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const workplanId = urlParams.get('workplan_id');
    setIsViewingWorkplan(!!workplanId || !!initialReportId);
    
    const handleLocationChange = () => {
      const newParams = new URLSearchParams(window.location.search);
      const newWorkplanId = newParams.get('workplan_id');
      setIsViewingWorkplan(!!newWorkplanId || !!initialReportId);
    };
    
    window.addEventListener('popstate', handleLocationChange);
    return () => window.removeEventListener('popstate', handleLocationChange);
  }, [initialReportId]);

  const handleMainTabChange = (tabId: string) => {
    setActiveMainTab(tabId);
    const url = new URL(window.location.href);
    url.searchParams.set('tab', tabId);
    if (tabId === 'search') {
      url.searchParams.set('subtab', activeSearchSubtab);
    } else {
      url.searchParams.delete('subtab');
    }
    window.history.pushState({}, '', url.toString());
  };

  const handleSubtabChange = (subtabId: string) => {
    setActiveSearchSubtab(subtabId);
    const url = new URL(window.location.href);
    url.searchParams.set('tab', 'search');
    url.searchParams.set('subtab', subtabId);
    window.history.pushState({}, '', url.toString());
  };

  // Search subtabs content
  const searchSubtabs = [
    {
      id: 'workplans',
      label: 'Workplans',
      icon: <FileText className="h-4 w-4" />,
      content: <WorkplanSearchPage />,
    },
    {
      id: 'technical',
      label: 'Technical Approaches',
      icon: <Lightbulb className="h-4 w-4" />,
      content: <TechnicalApproachesPage />,
    },
    {
      id: 'projects',
      label: 'Past Projects',
      icon: <Building2 className="h-4 w-4" />,
      content: <ProjectPortfolioPage />,
    },
  ];

  // Main tabs - Create Workplan first
  const mainTabs = [
    {
      id: 'builder',
      label: 'Create Workplan',
      icon: <Plus className="h-4 w-4" />,
      description: 'Generate new workplans using existing reports or templates',
      content: (selectedWorkplanReport || selectedTemplate) ? (() => {
        // Convert policy pack template to WorkplanTemplate format
        const policyPackTemplate = selectedTemplate ? {
          sections: (selectedTemplate.template.additional_sections || []).map((s: any) => ({
            name: s.name,
            description: s.description || '',
          })),
          default_questions: (selectedTemplate.template.additional_questions || []).map((q: any) => ({
            section: q.section || 'General',
            question: q.question || '',
          })),
          template_source: 'policy_pack_template',
          service_line: selectedTemplate.serviceLine,
        } : undefined;

        // Get past project ID from report metadata if available
        const reportMetadata = selectedWorkplanReport ? ((selectedWorkplanReport as any).metadata || (selectedWorkplanReport as any).meta || {}) : {};
        const pastProjectIdFromReport = reportMetadata.past_project_id;
        
        return (
          <WorkplanBuilder
            pastProjectId={pastProjectIdFromReport}
            pastProjectName={selectedWorkplanReport?.name || selectedTemplate?.serviceLine || 'New Workplan'}
            clientName={selectedWorkplanReport?.client_name || 'Client'}
            serviceLine={reportMetadata.service_line || selectedTemplate?.serviceLine}
            initialTemplate={policyPackTemplate}
            onComplete={(jobId) => {
              addToast({
                type: 'success',
                title: 'Workplan Generation Started',
                message: `Workplan generation job ${jobId} has been submitted`,
              });
              setSelectedWorkplanReport(null);
              setSelectedTemplate(null);
              setCreationMethod('report');
              queryClient.invalidateQueries({ queryKey: ['reports', projectId] });
            }}
            onCancel={() => {
              setSelectedWorkplanReport(null);
              setSelectedTemplate(null);
              setCreationMethod('report');
            }}
          />
        );
      })() : (
        <div className="max-w-6xl mx-auto space-y-6">
          {/* Method Selection */}
          <Card>
            <div className="p-6">
              <h2 className="text-2xl font-bold mb-4">Create New Workplan</h2>
              <p className="text-gray-600 mb-6">
                Choose how you want to create your workplan:
              </p>
              
              <div className="flex gap-2 mb-6">
                <Button
                  variant={creationMethod === 'report' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setCreationMethod('report')}
                  className="flex items-center gap-2"
                >
                  <FileText className="h-4 w-4" />
                  From Existing Workplan
                </Button>
                <Button
                  variant={creationMethod === 'template' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setCreationMethod('template')}
                  className="flex items-center gap-2"
                >
                  <ListChecks className="h-4 w-4" />
                  From Policy Pack Template
                </Button>
              </div>
            </div>
          </Card>

          {/* Workplan Reports Selection */}
          {creationMethod === 'report' && (
            <Card>
              <div className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold">Select Existing Workplan</h3>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setBuilderViewMode(builderViewMode === 'grid' ? 'list' : 'grid')}
                      title={builderViewMode === 'grid' ? 'Switch to list view' : 'Switch to grid view'}
                    >
                      {builderViewMode === 'grid' ? <ListIcon className="h-4 w-4" /> : <Grid className="h-4 w-4" />}
                    </Button>
                    <Select
                      value={builderSortBy}
                      onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setBuilderSortBy(e.target.value as any)}
                      className="w-32"
                    >
                      <option value="date">Date</option>
                      <option value="name">Name</option>
                      <option value="client">Client</option>
                      <option value="serviceLine">Service Line</option>
                    </Select>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setBuilderSortDirection(builderSortDirection === 'asc' ? 'desc' : 'asc')}
                    >
                      {builderSortDirection === 'asc' ? '↑' : '↓'}
                    </Button>
                  </div>
                </div>
                
                {/* Search Input */}
                <div className="mb-4">
                  <Input
                    placeholder="Search workplans by name or client..."
                    value={builderSearchQuery}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setBuilderSearchQuery(e.target.value)}
                    className="w-full"
                  />
                </div>

                {!reports ? (
                  <div className="flex justify-center py-8">
                    <LoadingSpinner />
                  </div>
                ) : (() => {
                  // Filter and sort reports
                  let displayReports = filteredReports;
                  
                  // Apply search filter
                  if (builderSearchQuery) {
                    const query = builderSearchQuery.toLowerCase();
                    displayReports = displayReports.filter((report: Report) => {
                      const name = (report.name || '').toLowerCase();
                      const client = (report.client_name || '').toLowerCase();
                      return name.includes(query) || client.includes(query);
                    });
                  }
                  
                  // Sort reports
                  displayReports = [...displayReports].sort((a: Report, b: Report) => {
                    const metadataA = (a as any).metadata || {};
                    const metadataB = (b as any).metadata || {};
                    let comparison = 0;
                    
                    switch (builderSortBy) {
                      case 'date':
                        comparison = new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
                        break;
                      case 'name':
                        comparison = (a.name || a.client_name || '').localeCompare(b.name || b.client_name || '');
                        break;
                      case 'client':
                        comparison = (a.client_name || '').localeCompare(b.client_name || '');
                        break;
                      case 'serviceLine':
                        comparison = (metadataA.service_line || '').localeCompare(metadataB.service_line || '');
                        break;
                    }
                    
                    return builderSortDirection === 'asc' ? comparison : -comparison;
                  });
                  
                  if (displayReports.length === 0) {
                    return (
                      <EmptyState
                        icon={<FileText className="h-12 w-12" />}
                        title="No Workplan Reports Found"
                        description={builderSearchQuery ? "No reports match your search criteria" : "Create workplan reports first to use them as templates"}
                      />
                    );
                  }
                  
                  if (builderViewMode === 'list') {
                    return (
                      <div className="space-y-2">
                        <table className="w-full">
                          <thead>
                            <tr className="border-b border-theme-border">
                              <th className="text-left p-2">Name</th>
                              <th className="text-left p-2">Client</th>
                              <th className="text-left p-2">Service Line</th>
                              <th className="text-left p-2">Date</th>
                              <th className="text-left p-2">Status</th>
                              <th className="text-left p-2 w-12"></th>
                            </tr>
                          </thead>
                          <tbody>
                            {displayReports.map((report: Report) => {
                              const metadata = (report as any).metadata || {};
                              return (
                                <tr
                                  key={report.id}
                                  className="border-b border-theme-border hover:bg-theme-surface"
                                >
                                  <td 
                                    className="p-2 font-medium cursor-pointer"
                                    onClick={() => setSelectedWorkplanReport(report)}
                                  >
                                    {report.name || report.client_name}
                                  </td>
                                  <td 
                                    className="p-2 cursor-pointer"
                                    onClick={() => setSelectedWorkplanReport(report)}
                                  >
                                    {report.client_name}
                                  </td>
                                  <td 
                                    className="p-2 cursor-pointer"
                                    onClick={() => setSelectedWorkplanReport(report)}
                                  >
                                    {metadata.service_line || '-'}
                                  </td>
                                  <td 
                                    className="p-2 cursor-pointer"
                                    onClick={() => setSelectedWorkplanReport(report)}
                                  >
                                    {new Date(report.created_at).toLocaleDateString()}
                                  </td>
                                  <td 
                                    className="p-2 cursor-pointer"
                                    onClick={() => setSelectedWorkplanReport(report)}
                                  >
                                    {report.status && (
                                      <Badge variant="outline" className="text-xs">
                                        {report.status}
                                      </Badge>
                                    )}
                                  </td>
                                  <td className="p-2">
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setSelectedReportId(report.id);
                                        setActionMenuOpen(report.id);
                                      }}
                                    >
                                      <MoreVertical className="h-4 w-4" />
                                    </Button>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    );
                  }
                  
                  return (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {displayReports.map((report: Report) => {
                        const metadata = (report as any).metadata || {};
                        return (
                          <Card
                            key={report.id}
                            className="p-4 hover:border-theme-primary transition-colors relative group"
                          >
                            <div 
                              className="cursor-pointer"
                              onClick={() => setSelectedWorkplanReport(report)}
                            >
                              <div className="flex items-start justify-between mb-2">
                                <h4 className="font-semibold flex-1">{report.name || report.client_name}</h4>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="opacity-0 group-hover:opacity-100 transition-opacity"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setSelectedReportId(report.id);
                                    setActionMenuOpen(report.id);
                                  }}
                                >
                                  <MoreVertical className="h-4 w-4" />
                                </Button>
                              </div>
                              <div className="space-y-1 text-sm text-gray-600">
                                <div className="flex items-center gap-2">
                                  <User className="h-4 w-4" />
                                  {report.client_name}
                                </div>
                                {metadata.service_line && (
                                  <div className="flex items-center gap-2">
                                    <Tag className="h-4 w-4" />
                                    {metadata.service_line}
                                  </div>
                                )}
                                <div className="flex items-center gap-2">
                                  <Calendar className="h-4 w-4" />
                                  {new Date(report.created_at).toLocaleDateString()}
                                </div>
                              </div>
                              {report.status && (
                                <Badge variant="outline" className="mt-2 text-xs">
                                  {report.status}
                                </Badge>
                              )}
                            </div>
                          </Card>
                        );
                      })}
                    </div>
                  );
                })()}
              </div>
            </Card>
          )}

          {/* Policy Pack Templates Selection */}
          {creationMethod === 'template' && (
            <WorkplanTemplateSelector
              templates={workplanTemplates}
              onSelectTemplate={(template) => setSelectedTemplate({ 
                serviceLine: template.serviceLine, 
                template: template.template 
              })}
              onPreviewTemplate={(template) => setPreviewTemplate({ 
                serviceLine: template.serviceLine, 
                template: template.template 
              })}
              isLoading={!policyPack}
              title="Select Service Line Template"
              description="Choose a pre-configured workplan template for your service line. Only final templates (20 sections, 20 questions) are shown. Temporary templates are automatically filtered out."
            />
          )}
        </div>
      ),
    },
    {
      id: 'search',
      label: 'Search Workplans',
      icon: <Search className="h-4 w-4" />,
      description: 'Search and browse workplans, technical approaches, and past projects',
      content: (
        <div className="space-y-4">
          {/* Subtabs for Search */}
          <div className="border-b border-theme-border">
            <nav className="flex space-x-8">
              {searchSubtabs.map((subtab) => (
                <button
                  key={subtab.id}
                  onClick={() => handleSubtabChange(subtab.id)}
                  className={`
                    py-4 px-1 border-b-2 font-medium text-sm transition-colors
                    ${activeSearchSubtab === subtab.id
                      ? 'border-theme-primary text-theme-primary'
                      : 'border-transparent text-theme-text-secondary hover:text-theme-text hover:border-theme-border'
                    }
                  `}
                >
                  <div className="flex items-center gap-2">
                    {subtab.icon}
                    {subtab.label}
                  </div>
                </button>
              ))}
            </nav>
          </div>
          
          {/* Subtab Content */}
          <div className="mt-4">
            {searchSubtabs.find(t => t.id === activeSearchSubtab)?.content}
          </div>
        </div>
      ),
    },
    {
      id: 'canonical',
      label: 'Canonical Data',
      icon: <Database className="h-4 w-4" />,
      description: 'View and manage baseline costs, suppliers, and service offerings',
      content: <CanonicalDataPage />,
    },
  ];

  return (
    <WorkplanGenerationProvider>
      <div className="space-y-6">
        {/* Workplan-Specific Filter Panel - Hide when viewing a workplan */}
        {!isViewingWorkplan && activeMainTab === 'search' && (
          <Card>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold">Workplan Filters</h3>
                <div className="flex gap-2">
                  {activeFilterCount > 0 && (
                    <Button variant="outline" size="sm" onClick={handleClearFilters}>
                      <X className="h-4 w-4 mr-2" />
                      Clear ({activeFilterCount})
                    </Button>
                  )}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setShowFilters(!showFilters)}
                  >
                    <Filter className="h-4 w-4 mr-2" />
                    {showFilters ? 'Hide' : 'Show'} Filters
                  </Button>
                </div>
              </div>

              {showFilters && (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-4 border-t">
                  <div>
                    <label className="block text-sm font-medium mb-1">Date From</label>
                    <Input
                      type="date"
                      value={filters.dateFrom}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFilters({ ...filters, dateFrom: e.target.value })}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Date To</label>
                    <Input
                      type="date"
                      value={filters.dateTo}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFilters({ ...filters, dateTo: e.target.value })}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Client Name</label>
                    <Input
                      value={filters.client}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFilters({ ...filters, client: e.target.value })}
                      placeholder="Filter by client"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Project Type</label>
                    <Input
                      value={filters.projectType}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFilters({ ...filters, projectType: e.target.value })}
                      placeholder="Filter by project type"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Status</label>
                    <Select
                      value={filters.status}
                      onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setFilters({ ...filters, status: e.target.value })}
                    >
                      <option value="all">All Statuses</option>
                      <option value="draft">Draft</option>
                      <option value="in_review">In Review</option>
                      <option value="approved">Approved</option>
                      <option value="submitted">Submitted</option>
                      <option value="archived">Archived</option>
                    </Select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Service Line</label>
                    <Select
                      value={filters.serviceLine}
                      onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setFilters({ ...filters, serviceLine: e.target.value })}
                    >
                      <option value="all">All Service Lines</option>
                      <option value="contaminated-sites">Contaminated Sites</option>
                      <option value="environmental-engineering">Environmental Engineering</option>
                      <option value="environmental-consulting">Environmental Consulting</option>
                      <option value="environmental-assessment">Environmental Assessment</option>
                      <option value="biology-eia">Biology/EIA</option>
                      <option value="iaq-hazmat">IAQ/HazMat</option>
                    </Select>
                  </div>
                </div>
              )}

              {/* Active Filter Chips */}
              {activeFilterCount > 0 && (
                <div className="flex flex-wrap gap-2 pt-2 border-t">
                  {filters.dateFrom && (
                    <Badge variant="default" className="flex items-center gap-1">
                      From: {new Date(filters.dateFrom).toLocaleDateString()}
                      <X
                        className="h-3 w-3 cursor-pointer"
                        onClick={() => setFilters({ ...filters, dateFrom: '' })}
                      />
                    </Badge>
                  )}
                  {filters.dateTo && (
                    <Badge variant="default" className="flex items-center gap-1">
                      To: {new Date(filters.dateTo).toLocaleDateString()}
                      <X
                        className="h-3 w-3 cursor-pointer"
                        onClick={() => setFilters({ ...filters, dateTo: '' })}
                      />
                    </Badge>
                  )}
                  {filters.client && (
                    <Badge variant="default" className="flex items-center gap-1">
                      Client: {filters.client}
                      <X
                        className="h-3 w-3 cursor-pointer"
                        onClick={() => setFilters({ ...filters, client: '' })}
                      />
                    </Badge>
                  )}
                  {filters.projectType && (
                    <Badge variant="default" className="flex items-center gap-1">
                      Type: {filters.projectType}
                      <X
                        className="h-3 w-3 cursor-pointer"
                        onClick={() => setFilters({ ...filters, projectType: '' })}
                      />
                    </Badge>
                  )}
                  {filters.status !== 'all' && (
                    <Badge variant="default" className="flex items-center gap-1">
                      Status: {filters.status}
                      <X
                        className="h-3 w-3 cursor-pointer"
                        onClick={() => setFilters({ ...filters, status: 'all' })}
                      />
                    </Badge>
                  )}
                  {filters.serviceLine !== 'all' && (
                    <Badge variant="default" className="flex items-center gap-1">
                      Service: {filters.serviceLine}
                      <X
                        className="h-3 w-3 cursor-pointer"
                        onClick={() => setFilters({ ...filters, serviceLine: 'all' })}
                      />
                    </Badge>
                  )}
                </div>
              )}
            </div>
          </Card>
        )}

        {/* Main Tabs */}
        <Tabs
          tabs={mainTabs}
          defaultTab={activeMainTab}
          onChange={handleMainTabChange}
        />

        {/* Quick Actions Modal */}
        {selectedReportId && actionMenuOpen === selectedReportId && (
          <Modal
            isOpen={true}
            onClose={() => {
              setActionMenuOpen(null);
              setSelectedReportId(null);
            }}
            title="Workplan Actions"
          >
            <div className="space-y-2">
              <Button
                variant="outline"
                className="w-full justify-start"
                onClick={() => handleClone(selectedReportId)}
              >
                <Copy className="h-4 w-4 mr-2" />
                Clone Workplan
              </Button>
              <Button
                variant="outline"
                className="w-full justify-start"
                onClick={() => handleArchive(selectedReportId)}
              >
                <Archive className="h-4 w-4 mr-2" />
                Archive Workplan
              </Button>
              <Button
                variant="outline"
                className="w-full justify-start"
                onClick={() => handleExport(selectedReportId, 'pdf')}
              >
                <Download className="h-4 w-4 mr-2" />
                Export PDF
              </Button>
              <Button
                variant="outline"
                className="w-full justify-start"
                onClick={() => handleExport(selectedReportId, 'markdown')}
              >
                <Download className="h-4 w-4 mr-2" />
                Export Markdown
              </Button>
              <div className="border-t pt-2 mt-2">
                <Button
                  variant="outline"
                  className="w-full justify-start text-red-600 hover:text-red-700"
                  onClick={() => handleDelete(selectedReportId)}
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Delete Workplan
                </Button>
              </div>
            </div>
          </Modal>
        )}

        {/* Template Preview Modal */}
        {previewTemplate && (
          <Modal
            isOpen={true}
            onClose={() => setPreviewTemplate(null)}
            title={`Template Preview: ${previewTemplate.serviceLine}`}
            size="xl"
          >
            <div className="space-y-4 max-h-[70vh] overflow-y-auto">
              <div>
                <h3 className="font-semibold mb-2">Sections ({previewTemplate.template.additional_sections?.length || 0})</h3>
                <div className="space-y-2">
                  {(previewTemplate.template.additional_sections || []).map((section: any, idx: number) => (
                    <Card key={idx} className="p-3">
                      <h4 className="font-medium">{section.name}</h4>
                      {section.description && (
                        <p className="text-sm text-gray-600 mt-1">{section.description}</p>
                      )}
                    </Card>
                  ))}
                </div>
              </div>
              <div>
                <h3 className="font-semibold mb-2">Questions ({previewTemplate.template.additional_questions?.length || 0})</h3>
                <div className="space-y-2">
                  {(previewTemplate.template.additional_questions || []).map((q: any, idx: number) => (
                    <Card key={idx} className="p-3">
                      <div className="text-sm font-medium text-gray-700 mb-1">{q.section || 'General'}</div>
                      <div className="text-sm">{q.question}</div>
                    </Card>
                  ))}
                </div>
              </div>
              <div className="flex gap-2">
                <Button
                  variant="default"
                  className="flex-1"
                  onClick={() => {
                    setSelectedTemplate(previewTemplate);
                    setPreviewTemplate(null);
                  }}
                >
                  Use This Template
                </Button>
                <Button
                  variant="outline"
                  onClick={() => setPreviewTemplate(null)}
                >
                  Cancel
                </Button>
              </div>
            </div>
          </Modal>
        )}
      </div>
    </WorkplanGenerationProvider>
  );
}
